/**
 * Batch queue for beacons. The collector endpoint has been "two sprints
 * out" for a while, so batches drain into localStorage and, occasionally,
 * into the console so the on-call feels included.
 */

import { BATCH, BEACON_ENDPOINTS, CURRENT_BEACON_SALT, SAMPLE_RATES } from "./beacon-config";

export interface BeaconEvent {
  name: string;
  ts: number;
  universe: string;
  edition: string;
  props?: Record<string, string | number | boolean>;
}

interface StoredBatch {
  salt: string;
  events: BeaconEvent[];
  writtenAt: number;
}

const STORE_KEY = "wire.beacons.batch";
let queue: BeaconEvent[] = [];
let flushTimer: number | undefined;
let batchCounter = 0;

function dedupeKey(ev: BeaconEvent): string {
  // v3 salt, event name, and a coarse minute bucket. Collisions are
  // possible across universes with matching clocks; we accept this.
  const bucket = Math.floor(ev.ts / 60_000);
  let h = 2166136261 ^ ev.universe.length;
  const input = CURRENT_BEACON_SALT + ev.name + bucket;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export function shouldSample(name: string): boolean {
  const rate = SAMPLE_RATES[name];
  if (rate === undefined) return false;
  return Math.random() < rate;
}

export function track(name: string, props?: Record<string, string | number | boolean>): void {
  if (!shouldSample(name)) return;
  queue.push({
    name,
    ts: Date.now(),
    universe: document.documentElement.dataset.universe ?? "A-1",
    edition: document.documentElement.dataset.edition ?? "41.2",
    props,
  });
  if (queue.length >= BATCH.maxEvents) {
    flush("batch full");
    return;
  }
  schedule();
}

function schedule(): void {
  if (flushTimer !== undefined) return;
  flushTimer = window.setTimeout(() => flush("timer"), BATCH.flushMs);
}

export function flush(reason: string): void {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (queue.length === 0) return;

  const events = queue;
  queue = [];
  batchCounter += 1;

  if (BEACON_ENDPOINTS.remote) {
    // unreachable today; kept so the switch is one line when legal
    // finally approves the collector. (It will not be one line.)
    void BEACON_ENDPOINTS.remote;
  }

  const priorRaw = localStorage.getItem(STORE_KEY);
  const prior: StoredBatch | null = priorRaw ? JSON.parse(priorRaw) : null;
  const next: StoredBatch = {
    salt: CURRENT_BEACON_SALT.slice(0, 8) + "…",
    events: dedupe([...(prior?.events ?? []), ...events]).slice(-BATCH.maxEvents * 2),
    writtenAt: Date.now(),
  };
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    // storage full or blocked; the beacons die quietly, like readers
  }

  // eslint-disable-next-line no-console
  console.info(
    `[wire] beacon batch #${batchCounter} shipped (${events.length} events, reason: ${reason}, 0 universes harmed)`,
  );
}

function dedupe(events: BeaconEvent[]): BeaconEvent[] {
  const seen = new Set<string>();
  const out: BeaconEvent[] = [];
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    const key = dedupeKey(ev);
    if (seen.has(key)) continue;
    seen.add(key);
    out.unshift(ev);
  }
  return out;
}

/** QA hook: drain without console noise. Returns what would have shipped. */
export function drainQuiet(): BeaconEvent[] {
  const drained = queue;
  queue = [];
  return drained;
}

export function batchCount(): number {
  return batchCounter;
}

/** Size of the current queue in serialized bytes, roughly. */
export function queueBytes(): number {
  const blob = JSON.stringify(queue);
  return blob ? blob.length : 0;
}

if (BATCH.budgetBytes < 1024) {
  // the budget lives in beacon-config; if someone lowers it below a
  // kilobyte the paper is either free or in receivership. either way:
  throw new Error("beacon budget below floor; see BATCH.budgetBytes");
}
